import React, { useState, useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import '../styles/Dashboard.css';
import { MdSecurity, MdDashboard, MdNotifications, MdSettings, MdLogout, MdPerson, MdRadar, MdSearch, MdAutoFixHigh, MdShield, MdInfo, MdStorage, MdDescription, MdSmartToy, MdChat, MdPersonSearch, MdExtension } from 'react-icons/md';
import { collection, query, orderBy, limit, getDocs, where } from 'firebase/firestore';
import { db } from '../firebase';
import { populateSampleData } from '../utils/sampleData';
import ThirdPartyLogs from '../components/ThirdPartyLogs';
import OnboardingPopup from '../components/OnboardingPopup';

const Dashboard = ({ user }) => {
  const [activeSidebarItem, setActiveSidebarItem] = useState('dashboard');
  const [activeLogTab, setActiveLogTab] = useState('audit');
  const [activeAiTab, setActiveAiTab] = useState('triage');
  const [chatOpen, setChatOpen] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [chatMessages, setChatMessages] = useState([
    {
      role: 'assistant',
      content: 'Hello! I\'m your AI Security Assistant. How can I help with your investigation today?'
    }
  ]);
  
  // Onboarding popup state
  const [showOnboardingPopup, setShowOnboardingPopup] = useState(true);

  // New state for storing data from Firestore
  const [alerts, setAlerts] = useState([]);
  const [networkLogs, setNetworkLogs] = useState([]);
  const [userActivities, setUserActivities] = useState([]);
  const [vulnerabilities, setVulnerabilities] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dataPopulated, setDataPopulated] = useState(false);

  // Load data when relevant sections are activated
  useEffect(() => {
    if (activeSidebarItem === 'alerts') {
      loadAlerts();
    } else if (activeSidebarItem === 'logs' && activeLogTab === 'network') {
      loadNetworkLogs();
    } else if (activeSidebarItem === 'logs' && activeLogTab === 'identity') {
      loadUserActivities();
    } else if (activeSidebarItem === 'posture') {
      loadVulnerabilities();
    }
  }, [activeSidebarItem, activeLogTab]);

  // Function to close the onboarding popup
  const handleCloseOnboardingPopup = () => {
    setShowOnboardingPopup(false);
  };

  // Function to load alerts from Firestore
  const loadAlerts = async () => {
    setIsLoading(true);
    try {
      const alertsQuery = query(
        collection(db, 'alerts'),
        orderBy('timestamp', 'desc'),
        limit(20)
      );
      
      const querySnapshot = await getDocs(alertsQuery);
      
      if (querySnapshot.empty) {
        setAlerts([]);
      } else {
        const alertsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setAlerts(alertsData);
      }
    } catch (error) {
      console.error('Error loading alerts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to load network logs from Firestore
  const loadNetworkLogs = async () => {
    setIsLoading(true);
    try {
      const logsQuery = query(
        collection(db, 'networkLogs'),
        orderBy('timestamp', 'desc'),
        limit(50)
      );
      
      const querySnapshot = await getDocs(logsQuery);
      
      if (querySnapshot.empty) {
        setNetworkLogs([]);
      } else {
        const logsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setNetworkLogs(logsData);
      }
    } catch (error) {
      console.error('Error loading network logs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to load user activities from Firestore
  const loadUserActivities = async () => {
    setIsLoading(true);
    try {
      const activitiesQuery = query(
        collection(db, 'userActivities'),
        orderBy('timestamp', 'desc'),
        limit(30)
      );
      
      const querySnapshot = await getDocs(activitiesQuery);
      
      if (querySnapshot.empty) {
        setUserActivities([]);
      } else {
        const activitiesData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setUserActivities(activitiesData);
      }
    } catch (error) {
      console.error('Error loading user activities:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to load vulnerabilities from Firestore
  const loadVulnerabilities = async () => {
    setIsLoading(true);
    try {
      const vulnsQuery = query(
        collection(db, 'vulnerabilities'),
        orderBy('cvssScore', 'desc'),
        limit(10)
      );
      
      const querySnapshot = await getDocs(vulnsQuery);
      
      if (querySnapshot.empty) {
        setVulnerabilities([]);
      } else {
        const vulnsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setVulnerabilities(vulnsData);
      }
    } catch (error) {
      console.error('Error loading vulnerabilities:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to populate sample data
  const handlePopulateData = async () => {
    setIsLoading(true);
    try {
      const result = await populateSampleData();
      if (result) {
        setDataPopulated(true);
        // Load data after populating
        if (activeSidebarItem === 'alerts') {
          loadAlerts();
        }
      }
    } catch (error) {
      console.error('Error populating data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    const auth = getAuth();
    auth.signOut();
  };

  const handleChatSend = () => {
    if (chatInput.trim() === '') return;
    
    // Add user message to chat
    const updatedMessages = [
      ...chatMessages,
      { role: 'user', content: chatInput }
    ];
    
    setChatMessages(updatedMessages);
    setChatInput('');
    
    // Simulate AI response after a short delay
    setTimeout(() => {
      setChatMessages([
        ...updatedMessages,
        { 
          role: 'assistant', 
          content: 'I\'ve analyzed your query and found related security events. Based on the pattern analysis, this appears to be a potential lateral movement attempt following the initial compromise. I recommend isolating the affected workstation while we conduct further investigation.'
        }
      ]);
    }, 1500);
  };

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    // Check if timestamp is a Firestore Timestamp
    if (timestamp.toDate) {
      const date = timestamp.toDate();
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } else {
      // Handle if it's a regular Date object or string
      const date = new Date(timestamp);
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    }
  };
  
  return (
    <div className="dashboard-container">
      {/* Onboarding Popup */}
      <OnboardingPopup isOpen={showOnboardingPopup} onClose={handleCloseOnboardingPopup} />
      
      {/* Sidebar */}
      <div className="sidebar">
        <div className="sidebar-header">
          <img src="/socfirstlogo.png" alt="SOC Logo" className="sidebar-logo-img" />
          <h3>SOC AI Platform</h3>
        </div>
        
        <div className="sidebar-menu">
          <div 
            className={`sidebar-item ${activeSidebarItem === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveSidebarItem('dashboard')}
          >
            <MdDashboard className="sidebar-icon" />
            <span>Dashboard</span>
          </div>
          
          <div 
            className={`sidebar-item ${activeSidebarItem === 'detection' ? 'active' : ''}`}
            onClick={() => setActiveSidebarItem('detection')}
          >
            <MdRadar className="sidebar-icon" />
            <span>Threat Detection</span>
          </div>
          
          <div 
            className={`sidebar-item ${activeSidebarItem === 'investigation' ? 'active' : ''}`}
            onClick={() => setActiveSidebarItem('investigation')}
          >
            <MdSearch className="sidebar-icon" />
            <span>Investigation</span>
          </div>
          
          <div 
            className={`sidebar-item ${activeSidebarItem === 'autonomous' ? 'active' : ''}`}
            onClick={() => setActiveSidebarItem('autonomous')}
          >
            <MdSmartToy className="sidebar-icon" />
            <span>Autonomous AI</span>
          </div>
          
          <div 
            className={`sidebar-item ${activeSidebarItem === 'response' ? 'active' : ''}`}
            onClick={() => setActiveSidebarItem('response')}
          >
            <MdAutoFixHigh className="sidebar-icon" />
            <span>Response</span>
          </div>
          
          <div 
            className={`sidebar-item ${activeSidebarItem === 'logs' ? 'active' : ''}`}
            onClick={() => setActiveSidebarItem('logs')}
          >
            <MdDescription className="sidebar-icon" />
            <span>Log Monitoring</span>
          </div>
          
          <div 
            className={`sidebar-item ${activeSidebarItem === 'posture' ? 'active' : ''}`}
            onClick={() => setActiveSidebarItem('posture')}
          >
            <MdShield className="sidebar-icon" />
            <span>Security Posture</span>
          </div>
          
          <div 
            className={`sidebar-item ${activeSidebarItem === 'intelligence' ? 'active' : ''}`}
            onClick={() => setActiveSidebarItem('intelligence')}
          >
            <MdInfo className="sidebar-icon" />
            <span>Threat Intel</span>
          </div>
          
          <div 
            className={`sidebar-item ${activeSidebarItem === 'alerts' ? 'active' : ''}`}
            onClick={() => setActiveSidebarItem('alerts')}
          >
            <MdNotifications className="sidebar-icon" />
            <span>Alerts</span>
          </div>
          
          <div 
            className={`sidebar-item ${activeSidebarItem === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveSidebarItem('settings')}
          >
            <MdSettings className="sidebar-icon" />
            <span>Settings</span>
          </div>
        </div>
        
        <div className="sidebar-footer">
          <div className="sidebar-item" onClick={handleLogout}>
            <MdLogout className="sidebar-icon" />
            <span>Logout</span>
          </div>
        </div>
      </div>
      
      {/* Main content */}
      <div className="main-dashboard">
        {/* Header */}
        <header className="dashboard-header">
          <h2>Security Dashboard</h2>
          <div className="user-profile">
            <span>{user.email}</span>
            <MdPerson className="profile-icon" />
          </div>
        </header>
        
        {/* Content */}
        <div className="dashboard-content">
          {activeSidebarItem === 'dashboard' && (
            <div className="dashboard-overview animated-element">
              <div className="banner-container animated-element">
                <img src="/banner.png" alt="SOC AI Platform Banner" className="dashboard-banner" />
              </div>
              <h3 className="animated-element">Welcome to your Security Operations Center</h3>
              <p className="animated-element">Your security dashboard is now ready. This is where you'll monitor and manage security events.</p>
              
              <div className="stats-container">
                <div className="stat-card animated-element">
                  <h4>Total Alerts</h4>
                  <p className="stat-value">0</p>
                </div>
                
                <div className="stat-card animated-element">
                  <h4>Critical Issues</h4>
                  <p className="stat-value">0</p>
                </div>
                
                <div className="stat-card animated-element">
                  <h4>System Status</h4>
                  <p className="stat-value">Healthy</p>
                </div>
              </div>
              
              <div className="recent-activity animated-element">
                <h4>Recent Activity</h4>
                <div className="activity-list">
                  <div className="activity-item">
                    <p>User login successful</p>
                    <span className="activity-time">Just now</span>
                  </div>
                  <div className="activity-item">
                    <p>System initialized</p>
                    <span className="activity-time">Just now</span>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {activeSidebarItem === 'detection' && (
            <div className="feature-container">
              <h3>AI-Powered Threat Detection</h3>
              
              <div className="feature-card">
                <h4>AI-Powered Detections</h4>
                <p>Uses machine learning and AI algorithms, informed by Google's threat intelligence (including Mandiant), to automatically detect known and unknown threats.</p>
                <div className="feature-actions">
                  <button className="btn-primary">View Detections</button>
                </div>
              </div>
              
              <div className="feature-card">
                <h4>Natural Language Detection Rule Creation</h4>
                <p>Create detection rules using natural language prompts.</p>
                <div className="nl-input-container">
                  <input 
                    type="text" 
                    className="nl-input" 
                    placeholder="Example: Create a rule to detect unusual PowerShell activity" 
                  />
                  <button className="btn-primary">Generate</button>
                </div>
              </div>
              
              <div className="feature-card">
                <h4>Anomaly Detection</h4>
                <p>Identifies suspicious activities and anomalies that deviate from normal behavior patterns.</p>
                <div className="anomaly-dashboard">
                  <div className="anomaly-item">
                    <span className="anomaly-severity high">High</span>
                    <p>Unusual authentication pattern detected</p>
                  </div>
                  <div className="anomaly-item">
                    <span className="anomaly-severity medium">Medium</span>
                    <p>Unexpected network traffic spike</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {activeSidebarItem === 'investigation' && (
            <div className="feature-container">
              <h3>Enhanced Threat Investigation</h3>
              
              <div className="feature-card">
                <h4>Natural Language Search</h4>
                <p>Search through security datasets using conversational queries.</p>
                <div className="nl-input-container">
                  <input 
                    type="text" 
                    className="nl-input" 
                    placeholder="Example: Show me failed login attempts in the last 24 hours" 
                  />
                  <button className="btn-primary">Search</button>
                </div>
              </div>
              
              <div className="feature-card">
                <h4>AI-Generated Summaries</h4>
                <p>Automatically generates summaries of security cases and alerts with context and insights.</p>
                <div className="summary-container">
                  <div className="summary-item">
                    <h5>Alert Summary: Potential Data Exfiltration</h5>
                    <p>This alert indicates unusual outbound traffic to an uncommon destination. The user account involved has elevated privileges and has accessed sensitive data shortly before this activity.</p>
                    <p><strong>Recommendation:</strong> Investigate user activity patterns and consider isolating the affected system.</p>
                  </div>
                </div>
              </div>
              
              <div className="feature-card">
                <h4>Attack Path Analysis</h4>
                <p>Visualize potential attack paths and identify high-risk exposures in your cloud environment.</p>
                <div className="feature-actions">
                  <button className="btn-primary">View Attack Paths</button>
                </div>
              </div>
            </div>
          )}
          
          {activeSidebarItem === 'response' && (
            <div className="feature-container">
              <h3>Faster and More Precise Response</h3>
              
              <div className="feature-card">
                <h4>AI-Driven Recommendations</h4>
                <p>Get actionable recommendations based on AI analysis and integrated threat intelligence.</p>
                <div className="recommendations-container">
                  <div className="recommendation-item">
                    <h5>Recommendation for Alert #1082</h5>
                    <p>Based on the alert characteristics and threat intelligence, this appears to be related to the HAFNIUM threat group.</p>
                    <ul>
                      <li>Isolate affected system</li>
                      <li>Block communication to detected C2 servers</li>
                      <li>Run memory forensics to identify persistence mechanisms</li>
                    </ul>
                    <button className="btn-primary">Apply Actions</button>
                  </div>
                </div>
              </div>
              
              <div className="feature-card">
                <h4>Automated Playbook Assistance</h4>
                <p>AI-assisted creation of automation playbooks for common response actions.</p>
                <div className="playbooks-container">
                  <div className="playbook-item">
                    <h5>Ransomware Response</h5>
                    <button className="btn-secondary">View</button>
                    <button className="btn-secondary">Run</button>
                  </div>
                  <div className="playbook-item">
                    <h5>Phishing Investigation</h5>
                    <button className="btn-secondary">View</button>
                    <button className="btn-secondary">Run</button>
                  </div>
                  <button className="btn-primary">Create New Playbook</button>
                </div>
              </div>
              
              <div className="feature-card">
                <h4>Agentic AI</h4>
                <p>Deploy AI agents designed to autonomously investigate alerts and gather evidence.</p>
                <div className="agents-container">
                  <div className="agent-item">
                    <h5>Alert Triage Agent</h5>
                    <span className="agent-status active">Active</span>
                    <p>Currently processing 3 alerts</p>
                  </div>
                  <div className="agent-item">
                    <h5>Threat Hunting Agent</h5>
                    <span className="agent-status inactive">Inactive</span>
                    <button className="btn-secondary">Activate</button>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {activeSidebarItem === 'posture' && (
            <div className="feature-container">
              <h3>Improved Security Posture Management</h3>
              
              <div className="feature-card">
                <h4>AI-Assisted Posture Recommendations</h4>
                <p>Receive AI-driven recommendations for improving Identity and Access Management (IAM) posture.</p>
                <div className="posture-container">
                  <div className="posture-item">
                    <h5>IAM Risk Score: 72/100</h5>
                    <div className="risk-meter">
                      <div className="risk-fill" style={{width: "72%"}}></div>
                    </div>
                    <ul>
                      <li>3 service accounts with excessive permissions</li>
                      <li>5 unused permissions identified</li>
                      <li>2 critical role assignments need review</li>
                    </ul>
                    <button className="btn-primary">View Recommendations</button>
                  </div>
                </div>
              </div>
              
              <div className="feature-card">
                <h4>Risk Prioritization</h4>
                <p>Prioritize vulnerabilities and misconfigurations based on potential impact.</p>
                <div className="vulnerabilities-container">
                  <div className="vulnerability-item high">
                    <h5>CVE-2023-12345</h5>
                    <span className="vuln-risk high">Critical</span>
                    <p>Affects 3 production systems</p>
                    <button className="btn-primary">Remediate</button>
                  </div>
                  <div className="vulnerability-item medium">
                    <h5>Misconfigured S3 Bucket</h5>
                    <span className="vuln-risk medium">Medium</span>
                    <p>Public read access enabled</p>
                    <button className="btn-primary">Remediate</button>
                  </div>
                </div>
              </div>
              
              <div className="feature-card">
                <h4>AI-Driven Data Classification</h4>
                <p>Discover and classify sensitive data across the environment.</p>
                <div className="classification-container">
                  <div className="classification-stats">
                    <div className="stat-item">
                      <h5>PII Data</h5>
                      <p>Found in 12 locations</p>
                    </div>
                    <div className="stat-item">
                      <h5>Financial Data</h5>
                      <p>Found in 5 locations</p>
                    </div>
                    <div className="stat-item">
                      <h5>Intellectual Property</h5>
                      <p>Found in 8 locations</p>
                    </div>
                  </div>
                  <button className="btn-primary">View Detailed Report</button>
                </div>
              </div>
            </div>
          )}
          
          {activeSidebarItem === 'intelligence' && (
            <div className="feature-container">
              <h3>Streamlined Threat Intelligence</h3>
              
              <div className="feature-card">
                <h4>AI-Powered Threat Briefings</h4>
                <p>Query Mandiant's frontline intelligence using natural language.</p>
                <div className="nl-input-container">
                  <input 
                    type="text" 
                    className="nl-input" 
                    placeholder="Example: Tell me about recent APT29 activities" 
                  />
                  <button className="btn-primary">Query</button>
                </div>
                
                <div className="intelligence-summary">
                  <h5>Recent Threat Actor Activities</h5>
                  <div className="actor-item">
                    <h5>APT28 (Fancy Bear)</h5>
                    <p>Recently observed targeting government organizations with spear-phishing campaigns delivering custom malware.</p>
                    <button className="btn-secondary">View Full Report</button>
                  </div>
                  <div className="actor-item">
                    <h5>LockBit Ransomware</h5>
                    <p>New variant identified with enhanced encryption capabilities and data exfiltration techniques.</p>
                    <button className="btn-secondary">View Full Report</button>
                  </div>
                </div>
              </div>
              
              <div className="feature-card">
                <h4>Threat Indicators</h4>
                <p>Access and manage threat indicators with AI-enhanced context.</p>
                <div className="indicators-container">
                  <div className="indicator-tabs">
                    <button className="tab active">IP Addresses</button>
                    <button className="tab">Domains</button>
                    <button className="tab">Hashes</button>
                    <button className="tab">YARA Rules</button>
                  </div>
                  <div className="indicator-list">
                    <div className="indicator-item">
                      <p>198.51.100.1</p>
                      <span className="indicator-confidence high">High</span>
                      <button className="btn-secondary">Add to Blocklist</button>
                    </div>
                    <div className="indicator-item">
                      <p>203.0.113.5</p>
                      <span className="indicator-confidence medium">Medium</span>
                      <button className="btn-secondary">Add to Blocklist</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {activeSidebarItem === 'alerts' && (
            <div className="alerts-container">
              <div className="alerts-header">
                <h3>Security Alerts</h3>
                {!dataPopulated && (
                  <button 
                    className="btn-primary" 
                    onClick={handlePopulateData}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Loading...' : 'Populate Sample Data'}
                  </button>
                )}
                {dataPopulated && (
                  <button 
                    className="btn-primary" 
                    onClick={loadAlerts}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Loading...' : 'Refresh Alerts'}
                  </button>
                )}
              </div>
              
              {isLoading ? (
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                  <p>Loading alerts...</p>
                </div>
              ) : alerts.length === 0 ? (
                <div className="empty-state animated-element">
                  <p>No alerts available. Click "Populate Sample Data" to add example security alerts.</p>
                </div>
              ) : (
                <div className="alerts-list">
                  {alerts.map((alert, index) => (
                    <div key={alert.id} className={`alert-item ${alert.severity} animated-element`} style={{animationDelay: `${index * 0.1}s`}}>
                      <div className="alert-header">
                        <div className="alert-title-container">
                          <span className={`alert-severity ${alert.severity}`}>{alert.severity.toUpperCase()}</span>
                          <h4 className="alert-title">{alert.title}</h4>
                        </div>
                        <span className="alert-time">{formatTimestamp(alert.timestamp)}</span>
                      </div>
                      <p className="alert-description">{alert.description}</p>
                      <div className="alert-details">
                        <div className="alert-detail">
                          <span className="detail-label">Source:</span>
                          <span className="detail-value">{alert.source}</span>
                        </div>
                        {alert.sourceIp && (
                          <div className="alert-detail">
                            <span className="detail-label">Source IP:</span>
                            <span className="detail-value">{alert.sourceIp}</span>
                          </div>
                        )}
                        {alert.destinationIp && (
                          <div className="alert-detail">
                            <span className="detail-label">Destination IP:</span>
                            <span className="detail-value">{alert.destinationIp}</span>
                          </div>
                        )}
                        <div className="alert-detail">
                          <span className="detail-label">Status:</span>
                          <span className={`detail-value status-${alert.status}`}>{alert.status}</span>
                        </div>
                      </div>
                      <div className="alert-tags">
                        {alert.tags && alert.tags.map((tag, index) => (
                          <span key={index} className="alert-tag">{tag}</span>
                        ))}
                      </div>
                      <div className="alert-actions">
                        <button className="btn-primary">Investigate</button>
                        <button className="btn-secondary">Assign</button>
                        <button className="btn-secondary">Close</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {activeSidebarItem === 'logs' && (
            <div className="feature-container">
              <h3>Log Monitoring & Analysis</h3>
              <p>Centralized visibility into all security-relevant logs and events across your environment.</p>
              
              <div className="logs-tabs">
                <button 
                  className={`log-tab ${activeLogTab === 'audit' ? 'active' : ''}`}
                  onClick={() => setActiveLogTab('audit')}
                >
                  Core Audit Logs
                </button>
                <button 
                  className={`log-tab ${activeLogTab === 'network' ? 'active' : ''}`}
                  onClick={() => setActiveLogTab('network')}
                >
                  Network Activity
                </button>
                <button 
                  className={`log-tab ${activeLogTab === 'identity' ? 'active' : ''}`}
                  onClick={() => setActiveLogTab('identity')}
                >
                  Identity & Access
                </button>
                <button 
                  className={`log-tab ${activeLogTab === 'services' ? 'active' : ''}`}
                  onClick={() => setActiveLogTab('services')}
                >
                  Service-Specific
                </button>
                <button 
                  className={`log-tab ${activeLogTab === 'security' ? 'active' : ''}`}
                  onClick={() => setActiveLogTab('security')}
                >
                  Security Platform
                </button>
                <button 
                  className={`log-tab ${activeLogTab === 'third-party' ? 'active' : ''}`}
                  onClick={() => setActiveLogTab('third-party')}
                >
                  Third-Party Logs
                </button>
              </div>
              
              {activeLogTab === 'audit' && (
                <div className="log-content">
                  <div className="feature-card">
                    <div className="log-header">
                      <h4>Cloud Audit Logs</h4>
                      <div className="log-controls">
                        <select className="log-select">
                          <option>Last 24 hours</option>
                          <option>Last 7 days</option>
                          <option>Last 30 days</option>
                          <option>Custom range</option>
                        </select>
                        <button className="btn-secondary">Export</button>
                        <button className="btn-primary">Refresh</button>
                      </div>
                    </div>
                    
                    <div className="log-type-container">
                      <div className="log-type-header active">
                        <h5>Admin Activity Logs</h5>
                        <span className="log-status enabled">Always Enabled</span>
                      </div>
                      <div className="log-entries">
                        <div className="log-entry">
                          <div className="log-entry-header">
                            <span className="log-timestamp">2023-11-05 14:32:45</span>
                            <span className="log-severity info">INFO</span>
                          </div>
                          <p className="log-message">User john@example.com created a new VM instance 'prod-server-1'</p>
                          <button className="btn-secondary btn-sm">Details</button>
                        </div>
                        <div className="log-entry">
                          <div className="log-entry-header">
                            <span className="log-timestamp">2023-11-05 13:15:20</span>
                            <span className="log-severity warning">WARNING</span>
                          </div>
                          <p className="log-message">IAM policy modified for project 'soc-platform-prod'</p>
                          <button className="btn-secondary btn-sm">Details</button>
                        </div>
                      </div>
                    </div>
                    
                    <div className="log-type-container">
                      <div className="log-type-header">
                        <h5>Data Access Logs</h5>
                        <span className="log-status selective">Selective Enablement</span>
                      </div>
                      <div className="log-config">
                        <p>Data Access Logs are disabled by default (except BigQuery). Enable for critical resources.</p>
                        <button className="btn-primary">Configure Data Access Logging</button>
                      </div>
                    </div>
                    
                    <div className="log-type-container">
                      <div className="log-type-header">
                        <h5>System Event Logs</h5>
                        <span className="log-status enabled">Always Enabled</span>
                      </div>
                      <div className="log-entries">
                        <div className="log-entry">
                          <div className="log-entry-header">
                            <span className="log-timestamp">2023-11-05 09:12:33</span>
                            <span className="log-severity info">INFO</span>
                          </div>
                          <p className="log-message">Scheduled maintenance completed for Compute Engine instance 'app-server-3'</p>
                          <button className="btn-secondary btn-sm">Details</button>
                        </div>
                      </div>
                    </div>
                    
                    <div className="log-type-container">
                      <div className="log-type-header">
                        <h5>Policy Denied Logs</h5>
                        <span className="log-status enabled">Generated by Default</span>
                      </div>
                      <div className="log-entries">
                        <div className="log-entry">
                          <div className="log-entry-header">
                            <span className="log-timestamp">2023-11-05 11:45:07</span>
                            <span className="log-severity error">ERROR</span>
                          </div>
                          <p className="log-message">Access denied: User alex@example.com attempted to access BigQuery dataset 'finance-data'</p>
                          <button className="btn-secondary btn-sm">Details</button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {activeLogTab === 'network' && (
                <div className="network-logs-container">
                  {networkLogs.length === 0 ? (
                    <div className="empty-state">
                      <p>No network logs available. Click "Populate Sample Data" to add example network logs.</p>
                      <button 
                        className="btn-primary" 
                        onClick={handlePopulateData}
                        disabled={isLoading}
                      >
                        {isLoading ? 'Loading...' : 'Populate Sample Data'}
                      </button>
                    </div>
                  ) : (
                    <div className="logs-table-container">
                      <table className="logs-table">
                        <thead>
                          <tr>
                            <th>Time</th>
                            <th>Source IP</th>
                            <th>Destination IP</th>
                            <th>Protocol</th>
                            <th>Bytes</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {networkLogs.map(log => (
                            <tr key={log.id} className={log.action === 'blocked' ? 'blocked-log' : ''}>
                              <td>{formatTimestamp(log.timestamp)}</td>
                              <td>{log.sourceIp}</td>
                              <td>{log.destinationIp}</td>
                              <td>{log.protocol}</td>
                              <td>{log.bytesTransferred.toLocaleString()}</td>
                              <td>
                                <span className={`log-action ${log.action}`}>
                                  {log.action}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
              
              {activeLogTab === 'identity' && (
                <div className="user-activities-container">
                  {userActivities.length === 0 ? (
                    <div className="empty-state">
                      <p>No user activity data available. Click "Populate Sample Data" to add example user activities.</p>
                      <button 
                        className="btn-primary" 
                        onClick={handlePopulateData}
                        disabled={isLoading}
                      >
                        {isLoading ? 'Loading...' : 'Populate Sample Data'}
                      </button>
                    </div>
                  ) : (
                    <div className="activities-list">
                      {userActivities.map(activity => (
                        <div key={activity.id} className={`activity-item ${!activity.successful ? 'failed' : ''}`}>
                          <div className="activity-header">
                            <div className="activity-title">
                              <span className="activity-type">{activity.activityType.replace('_', ' ')}</span>
                              <span className="activity-user">{activity.userName}</span>
                            </div>
                            <span className="activity-time">{formatTimestamp(activity.timestamp)}</span>
                          </div>
                          <div className="activity-details">
                            <div className="activity-detail">
                              <span className="detail-label">Department:</span>
                              <span className="detail-value">{activity.department}</span>
                            </div>
                            <div className="activity-detail">
                              <span className="detail-label">IP Address:</span>
                              <span className="detail-value">{activity.ipAddress}</span>
                            </div>
                            <div className="activity-detail">
                              <span className="detail-label">Location:</span>
                              <span className="detail-value">{activity.location}</span>
                            </div>
                            <div className="activity-detail">
                              <span className="detail-label">Device:</span>
                              <span className="detail-value">{activity.device}</span>
                            </div>
                            <div className="activity-detail">
                              <span className="detail-label">Status:</span>
                              <span className={`detail-value ${activity.successful ? 'success' : 'failed'}`}>
                                {activity.successful ? 'Successful' : 'Failed'}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {activeLogTab === 'services' && (
                <div className="log-content">
                  <div className="feature-card">
                    <div className="log-header">
                      <h4>Service-Specific Logs</h4>
                      <div className="log-controls">
                        <select className="log-select">
                          <option>Last 24 hours</option>
                          <option>Last 7 days</option>
                          <option>Last 30 days</option>
                          <option>Custom range</option>
                        </select>
                        <button className="btn-secondary">Export</button>
                        <button className="btn-primary">Refresh</button>
                      </div>
                    </div>
                    
                    <div className="services-grid">
                      <div className="service-log-card">
                        <div className="service-header">
                          <h5>Compute Engine</h5>
                          <span className="log-status configurable">Configurable</span>
                        </div>
                        <ul className="service-log-types">
                          <li>Agent logs</li>
                          <li>Serial port output</li>
                          <li>System event logs</li>
                        </ul>
                        <button className="btn-primary btn-sm">Configure</button>
                      </div>
                      
                      <div className="service-log-card">
                        <div className="service-header">
                          <h5>Google Kubernetes Engine</h5>
                          <span className="log-status configurable">Configurable</span>
                        </div>
                        <ul className="service-log-types">
                          <li>Cluster audit logs</li>
                          <li>Application logs</li>
                          <li>Node logs</li>
                        </ul>
                        <button className="btn-primary btn-sm">Configure</button>
                      </div>
                      
                      <div className="service-log-card">
                        <div className="service-header">
                          <h5>Cloud Storage</h5>
                          <span className="log-status configurable">Configurable</span>
                        </div>
                        <ul className="service-log-types">
                          <li>Access logs</li>
                          <li>Object operations</li>
                        </ul>
                        <button className="btn-primary btn-sm">Configure</button>
                      </div>
                      
                      <div className="service-log-card">
                        <div className="service-header">
                          <h5>Cloud SQL</h5>
                          <span className="log-status configurable">Configurable</span>
                        </div>
                        <ul className="service-log-types">
                          <li>Database audit logs</li>
                          <li>Error logs</li>
                        </ul>
                        <button className="btn-primary btn-sm">Configure</button>
                      </div>
                      
                      <div className="service-log-card">
                        <div className="service-header">
                          <h5>BigQuery</h5>
                          <span className="log-status enabled">Data Access Enabled</span>
                        </div>
                        <ul className="service-log-types">
                          <li>Query logs</li>
                          <li>Table modifications</li>
                          <li>Data access logs</li>
                        </ul>
                        <button className="btn-primary btn-sm">View Logs</button>
                      </div>
                      
                      <div className="service-log-card">
                        <div className="service-header">
                          <h5>Serverless</h5>
                          <span className="log-status configurable">Configurable</span>
                        </div>
                        <ul className="service-log-types">
                          <li>Cloud Functions logs</li>
                          <li>Cloud Run logs</li>
                          <li>App Engine logs</li>
                        </ul>
                        <button className="btn-primary btn-sm">Configure</button>
                      </div>
                    </div>
                    
                    <div className="service-log-integrations">
                      <h5>Log Destinations</h5>
                      <div className="destination-options">
                        <div className="destination-option">
                          <input type="checkbox" id="cloud-logging" checked />
                          <label htmlFor="cloud-logging">Cloud Logging</label>
                        </div>
                        <div className="destination-option">
                          <input type="checkbox" id="security-operations" checked />
                          <label htmlFor="security-operations">Google Security Operations</label>
                        </div>
                        <div className="destination-option">
                          <input type="checkbox" id="bigquery" />
                          <label htmlFor="bigquery">BigQuery</label>
                        </div>
                        <div className="destination-option">
                          <input type="checkbox" id="pubsub" />
                          <label htmlFor="pubsub">Pub/Sub</label>
                        </div>
                      </div>
                      <button className="btn-primary">Configure Log Routing</button>
                    </div>
                  </div>
                </div>
              )}
              
              {activeLogTab === 'security' && (
                <div className="log-content">
                  <div className="feature-card">
                    <div className="log-header">
                      <h4>Security Platform Logs</h4>
                      <div className="log-controls">
                        <select className="log-select">
                          <option>Last 24 hours</option>
                          <option>Last 7 days</option>
                          <option>Last 30 days</option>
                          <option>Custom range</option>
                        </select>
                        <button className="btn-secondary">Export</button>
                        <button className="btn-primary">Refresh</button>
                      </div>
                    </div>
                    
                    <div className="log-items">
                      <div className="log-item">
                        <span className="log-time">2023-06-15 09:45:22</span>
                        <span className="log-level info">INFO</span>
                        <span className="log-message">Security scanner completed scan of project assets</span>
                      </div>
                      <div className="log-item">
                        <span className="log-time">2023-06-15 09:43:15</span>
                        <span className="log-level warning">WARNING</span>
                        <span className="log-message">Vulnerability scan detected 3 low severity issues</span>
                      </div>
                      <div className="log-item">
                        <span className="log-time">2023-06-15 09:40:01</span>
                        <span className="log-level info">INFO</span>
                        <span className="log-message">Security scanner initiated scan of project assets</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {activeLogTab === 'third-party' && (
                <div className="log-content">
                  <ThirdPartyLogs />
                </div>
              )}
            </div>
          )}
          
          {activeSidebarItem === 'autonomous' && (
            <div className="feature-container">
              <h3>Autonomous Alert Investigation</h3>
              <p>Powered by Google Vertex AI to autonomously investigate and triage security alerts at scale.</p>
              
              <div className="ai-tabs">
                <button 
                  className={`ai-tab ${activeAiTab === 'triage' ? 'active' : ''}`}
                  onClick={() => setActiveAiTab('triage')}
                >
                  Autonomous Triage
                </button>
                <button 
                  className={`ai-tab ${activeAiTab === 'integration' ? 'active' : ''}`}
                  onClick={() => setActiveAiTab('integration')}
                >
                  Data Integration
                </button>
                <button 
                  className={`ai-tab ${activeAiTab === 'context' ? 'active' : ''}`}
                  onClick={() => setActiveAiTab('context')}
                >
                  Context Awareness
                </button>
                <button 
                  className={`ai-tab ${activeAiTab === 'reports' ? 'active' : ''}`}
                  onClick={() => setActiveAiTab('reports')}
                >
                  Reports
                </button>
                <button 
                  className={`ai-tab ${activeAiTab === 'tools' ? 'active' : ''}`}
                  onClick={() => setActiveAiTab('tools')}
                >
                  AI Tools
                </button>
              </div>
              
              {activeAiTab === 'triage' && (
                <div className="ai-content">
                  <div className="feature-card">
                    <div className="feature-header">
                      <h4>Automated Triage & Investigation</h4>
                      <span className="ai-status active">Active</span>
                    </div>
                    <p>Vertex AI autonomously investigates security alerts (Tier 1) without requiring manual playbooks, code, or prompts - mimicking the reasoning of expert human analysts.</p>
                    
                    <div className="alert-types-grid">
                      <div className="alert-type-card">
                        <h5>Phishing Alerts</h5>
                        <div className="ai-stat">
                          <span className="stat-label">Auto-triaged</span>
                          <span className="stat-value">24/28</span>
                        </div>
                        <div className="progress-bar">
                          <div className="progress-fill" style={{width: "85%"}}></div>
                        </div>
                      </div>
                      <div className="alert-type-card">
                        <h5>Endpoint Threats</h5>
                        <div className="ai-stat">
                          <span className="stat-label">Auto-triaged</span>
                          <span className="stat-value">32/35</span>
                        </div>
                        <div className="progress-bar">
                          <div className="progress-fill" style={{width: "91%"}}></div>
                        </div>
                      </div>
                      <div className="alert-type-card">
                        <h5>Network Issues</h5>
                        <div className="ai-stat">
                          <span className="stat-label">Auto-triaged</span>
                          <span className="stat-value">18/21</span>
                        </div>
                        <div className="progress-bar">
                          <div className="progress-fill" style={{width: "85%"}}></div>
                        </div>
                      </div>
                      <div className="alert-type-card">
                        <h5>Cloud Security</h5>
                        <div className="ai-stat">
                          <span className="stat-label">Auto-triaged</span>
                          <span className="stat-value">15/16</span>
                        </div>
                        <div className="progress-bar">
                          <div className="progress-fill" style={{width: "93%"}}></div>
                        </div>
                      </div>
                      <div className="alert-type-card">
                        <h5>Identity Events</h5>
                        <div className="ai-stat">
                          <span className="stat-label">Auto-triaged</span>
                          <span className="stat-value">19/25</span>
                        </div>
                        <div className="progress-bar">
                          <div className="progress-fill" style={{width: "76%"}}></div>
                        </div>
                      </div>
                      <div className="alert-type-card">
                        <h5>Insider Threats</h5>
                        <div className="ai-stat">
                          <span className="stat-label">Auto-triaged</span>
                          <span className="stat-value">8/10</span>
                        </div>
                        <div className="progress-bar">
                          <div className="progress-fill" style={{width: "80%"}}></div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="ai-metrics">
                      <div className="ai-metric-card">
                        <h5>Average Triage Time</h5>
                        <div className="metric-value">3.2 min</div>
                        <div className="metric-comparison">
                          <span className="comparison-arrow down">↓</span>
                          <span className="comparison-value">93%</span>
                          <span className="comparison-label">vs. manual</span>
                        </div>
                      </div>
                      <div className="ai-metric-card">
                        <h5>Accuracy Rate</h5>
                        <div className="metric-value">94.8%</div>
                        <div className="metric-comparison">
                          <span className="comparison-arrow up">↑</span>
                          <span className="comparison-value">12%</span>
                          <span className="comparison-label">vs. previous month</span>
                        </div>
                      </div>
                      <div className="ai-metric-card">
                        <h5>Alerts Auto-Resolved</h5>
                        <div className="metric-value">68%</div>
                        <div className="metric-comparison">
                          <span className="comparison-arrow up">↑</span>
                          <span className="comparison-value">23%</span>
                          <span className="comparison-label">vs. previous month</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="in-progress-container">
                      <h5>Currently Investigating</h5>
                      <div className="in-progress-alerts">
                        <div className="in-progress-alert">
                          <div className="alert-info">
                            <span className="alert-id">ALERT-2023-11-08-0042</span>
                            <span className="alert-type">Suspicious Login Activity</span>
                          </div>
                          <div className="investigation-progress">
                            <div className="progress-label">67% Complete</div>
                            <div className="progress-bar">
                              <div className="progress-fill" style={{width: "67%"}}></div>
                            </div>
                          </div>
                        </div>
                        <div className="in-progress-alert">
                          <div className="alert-info">
                            <span className="alert-id">ALERT-2023-11-08-0043</span>
                            <span className="alert-type">Potential Data Exfiltration</span>
                          </div>
                          <div className="investigation-progress">
                            <div className="progress-label">34% Complete</div>
                            <div className="progress-bar">
                              <div className="progress-fill" style={{width: "34%"}}></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="actions-container">
                      <button className="btn-primary">View All Investigations</button>
                      <button className="btn-secondary">Configure AI Settings</button>
                    </div>
                  </div>
                </div>
              )}
              
              {activeAiTab === 'integration' && (
                <div className="ai-content">
                  <div className="feature-card">
                    <h4>Data Collection & Integration</h4>
                    <p>Vertex AI connects to over 60 security and IT tools to automatically gather relevant data for investigations.</p>
                    
                    <div className="integration-categories">
                      <div className="integration-category">
                        <h5>SIEM & Log Management</h5>
                        <div className="integration-tools">
                          <div className="integration-tool connected">
                            <span className="tool-name">Splunk</span>
                            <span className="tool-status">Connected</span>
                          </div>
                          <div className="integration-tool connected">
                            <span className="tool-name">IBM QRadar</span>
                            <span className="tool-status">Connected</span>
                          </div>
                          <div className="integration-tool connected">
                            <span className="tool-name">Google Security Operations</span>
                            <span className="tool-status">Connected</span>
                          </div>
                          <div className="integration-tool">
                            <span className="tool-name">Elastic Security</span>
                            <span className="tool-status">Available</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="integration-category">
                        <h5>Endpoint Security</h5>
                        <div className="integration-tools">
                          <div className="integration-tool connected">
                            <span className="tool-name">CrowdStrike</span>
                            <span className="tool-status">Connected</span>
                          </div>
                          <div className="integration-tool connected">
                            <span className="tool-name">SentinelOne</span>
                            <span className="tool-status">Connected</span>
                          </div>
                          <div className="integration-tool">
                            <span className="tool-name">Microsoft Defender</span>
                            <span className="tool-status">Available</span>
                          </div>
                          <div className="integration-tool">
                            <span className="tool-name">Carbon Black</span>
                            <span className="tool-status">Available</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="integration-category">
                        <h5>Cloud Platforms</h5>
                        <div className="integration-tools">
                          <div className="integration-tool connected">
                            <span className="tool-name">Google Cloud Platform</span>
                            <span className="tool-status">Connected</span>
                          </div>
                          <div className="integration-tool connected">
                            <span className="tool-name">AWS</span>
                            <span className="tool-status">Connected</span>
                          </div>
                          <div className="integration-tool connected">
                            <span className="tool-name">Microsoft Azure</span>
                            <span className="tool-status">Connected</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="integration-category">
                        <h5>Identity Providers</h5>
                        <div className="integration-tools">
                          <div className="integration-tool connected">
                            <span className="tool-name">Okta</span>
                            <span className="tool-status">Connected</span>
                          </div>
                          <div className="integration-tool">
                            <span className="tool-name">Microsoft Entra ID</span>
                            <span className="tool-status">Available</span>
                          </div>
                          <div className="integration-tool">
                            <span className="tool-name">Ping Identity</span>
                            <span className="tool-status">Available</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="integration-category">
                        <h5>Network Security</h5>
                        <div className="integration-tools">
                          <div className="integration-tool connected">
                            <span className="tool-name">Palo Alto Networks</span>
                            <span className="tool-status">Connected</span>
                          </div>
                          <div className="integration-tool connected">
                            <span className="tool-name">Cisco Secure Firewall</span>
                            <span className="tool-status">Connected</span>
                          </div>
                          <div className="integration-tool">
                            <span className="tool-name">Fortinet</span>
                            <span className="tool-status">Available</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="integration-category">
                        <h5>Email Security</h5>
                        <div className="integration-tools">
                          <div className="integration-tool connected">
                            <span className="tool-name">Microsoft 365</span>
                            <span className="tool-status">Connected</span>
                          </div>
                          <div className="integration-tool connected">
                            <span className="tool-name">Google Workspace</span>
                            <span className="tool-status">Connected</span>
                          </div>
                          <div className="integration-tool">
                            <span className="tool-name">Proofpoint</span>
                            <span className="tool-status">Available</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="actions-container">
                      <button className="btn-primary">Connect New Tool</button>
                      <button className="btn-secondary">View All Integrations</button>
                    </div>
                  </div>
                </div>
              )}
              
              {activeAiTab === 'context' && (
                <div className="ai-content">
                  <div className="feature-card">
                    <h4>Context Awareness</h4>
                    <p>Vertex AI learns and remembers your organization's specific context to improve the accuracy of investigations.</p>
                    
                    <div className="context-summary">
                      <div className="context-item">
                        <h5>Organizational Knowledge</h5>
                        <div className="context-status">
                          <div className="context-indicator high"></div>
                          <span className="context-level">High Confidence</span>
                        </div>
                        <p>The AI system has built a strong understanding of your organization's structure, departments, and key stakeholders.</p>
                      </div>
                      
                      <div className="context-item">
                        <h5>Network Topology</h5>
                        <div className="context-status">
                          <div className="context-indicator high"></div>
                          <span className="context-level">High Confidence</span>
                        </div>
                        <p>IP ranges, critical systems, network segments, and normal traffic patterns have been learned and continuously updated.</p>
                      </div>
                      
                      <div className="context-item">
                        <h5>User Behavior</h5>
                        <div className="context-status">
                          <div className="context-indicator medium"></div>
                          <span className="context-level">Medium Confidence</span>
                        </div>
                        <p>Baseline user behavior patterns are established for most employees, with ongoing refinement to reduce false positives.</p>
                      </div>
                      
                      <div className="context-item">
                        <h5>Application Inventory</h5>
                        <div className="context-status">
                          <div className="context-indicator high"></div>
                          <span className="context-level">High Confidence</span>
                        </div>
                        <p>Comprehensive understanding of approved applications, internal tools, and expected behaviors.</p>
                      </div>
                    </div>
                    
                    <div className="context-learning">
                      <h5>Recent Context Learning</h5>
                      <div className="learning-timeline">
                        <div className="timeline-item">
                          <span className="timeline-date">Nov 7, 2023</span>
                          <div className="timeline-content">
                            <h6>Network Segment Update</h6>
                            <p>Identified and learned new development subnet (10.12.45.0/24) based on observed traffic patterns.</p>
                          </div>
                        </div>
                        <div className="timeline-item">
                          <span className="timeline-date">Nov 5, 2023</span>
                          <div className="timeline-content">
                            <h6>User Behavior Update</h6>
                            <p>Refined behavior baselines for Engineering team to account for increased after-hours work during deployment period.</p>
                          </div>
                        </div>
                        <div className="timeline-item">
                          <span className="timeline-date">Nov 3, 2023</span>
                          <div className="timeline-content">
                            <h6>New Application Added</h6>
                            <p>Detected and classified new CI/CD pipeline tool as authorized based on widespread legitimate usage.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="actions-container">
                      <button className="btn-primary">Update Context</button>
                      <button className="btn-secondary">Review Learning History</button>
                    </div>
                  </div>
                </div>
              )}
              
              {activeAiTab === 'reports' && (
                <div className="ai-content">
                  <div className="feature-card">
                    <h4>Decision-Ready Reports</h4>
                    <p>Vertex AI generates detailed reports for each investigation, including severity conclusions, executive summaries, key findings, and supporting evidence.</p>
                    
                    <div className="reports-filter">
                      <select className="report-select">
                        <option>All Reports</option>
                        <option>High Severity</option>
                        <option>Medium Severity</option>
                        <option>Low Severity</option>
                        <option>Benign</option>
                      </select>
                      <input type="text" placeholder="Search reports..." className="report-search" />
                    </div>
                    
                    <div className="reports-list">
                      <div className="report-item high">
                        <div className="report-header">
                          <div className="report-title-container">
                            <span className="report-severity high">High</span>
                            <h5 className="report-title">Potential Data Exfiltration</h5>
                          </div>
                          <span className="report-time">2 hours ago</span>
                        </div>
                        <div className="report-summary">
                          <p><strong>Executive Summary:</strong> Detected unusual outbound data transfer of 2.3GB to an unrecognized external IP address (185.132.45.23) from a Finance department workstation. The user account involved has accessed sensitive financial documents shortly before the transfer.</p>
                        </div>
                        <div className="report-actions">
                          <button className="btn-primary">View Full Report</button>
                          <button className="btn-secondary">Escalate</button>
                        </div>
                      </div>
                      
                      <div className="report-item medium">
                        <div className="report-header">
                          <div className="report-title-container">
                            <span className="report-severity medium">Medium</span>
                            <h5 className="report-title">Suspicious Authentication Activity</h5>
                          </div>
                          <span className="report-time">5 hours ago</span>
                        </div>
                        <div className="report-summary">
                          <p><strong>Executive Summary:</strong> Detected multiple failed login attempts followed by a successful login for user jsmith@example.com from an IP address (104.28.76.192) not previously associated with this user. Geolocation indicates access from a different country than usual work location.</p>
                        </div>
                        <div className="report-actions">
                          <button className="btn-primary">View Full Report</button>
                          <button className="btn-secondary">Escalate</button>
                        </div>
                      </div>
                      
                      <div className="report-item low">
                        <div className="report-header">
                          <div className="report-title-container">
                            <span className="report-severity low">Low</span>
                            <h5 className="report-title">Unusual Software Installation</h5>
                          </div>
                          <span className="report-time">Yesterday</span>
                        </div>
                        <div className="report-summary">
                          <p><strong>Executive Summary:</strong> Detected installation of unapproved software (FileZilla) on workstation DEV-WS-042. User has legitimate development role, and software has benign reputation. No suspicious activity observed after installation.</p>
                        </div>
                        <div className="report-actions">
                          <button className="btn-primary">View Full Report</button>
                          <button className="btn-secondary">Escalate</button>
                        </div>
                      </div>
                      
                      <div className="report-item benign">
                        <div className="report-header">
                          <div className="report-title-container">
                            <span className="report-severity benign">Benign</span>
                            <h5 className="report-title">Cloud Storage Access Alert</h5>
                          </div>
                          <span className="report-time">Yesterday</span>
                        </div>
                        <div className="report-summary">
                          <p><strong>Executive Summary:</strong> Alert triggered for access to sensitive cloud storage bucket. Investigation confirmed authorized access by IT admin account during scheduled maintenance window, consistent with change management record #CM-2023-0458.</p>
                        </div>
                        <div className="report-actions">
                          <button className="btn-primary">View Full Report</button>
                          <button className="btn-secondary">Reanalyze</button>
                        </div>
                      </div>
                    </div>
                    
                    <div className="pagination">
                      <button className="pagination-btn active">1</button>
                      <button className="pagination-btn">2</button>
                      <button className="pagination-btn">3</button>
                      <span className="pagination-separator">...</span>
                      <button className="pagination-btn">12</button>
                    </div>
                  </div>
                </div>
              )}
              
              {activeAiTab === 'tools' && (
                <div className="ai-content">
                  <div className="feature-card">
                    <h4>AI Security Tools</h4>
                    <p>Vertex AI provides several specialized tools to enhance security analysis and investigation capabilities.</p>
                    
                    <div className="tools-grid">
                      <div className="tool-card">
                        <div className="tool-header">
                          <MdChat className="tool-icon" />
                          <h5>AI Security Chatbot</h5>
                        </div>
                        <p>Interactive chatbot allowing analysts to conduct ad-hoc investigations, ask specific questions, or delve deeper into alerts.</p>
                        <div className="tool-status-bar">
                          <span className="tool-status active">Active</span>
                          <button className="btn-primary" onClick={() => setChatOpen(true)}>Open Chat</button>
                        </div>
                      </div>
                      
                      <div className="tool-card">
                        <div className="tool-header">
                          <MdPersonSearch className="tool-icon" />
                          <h5>AI Interviewer</h5>
                        </div>
                        <p>Automated security user interviews through messaging platforms (like Slack) to speed up investigations.</p>
                        <div className="tool-metrics">
                          <div className="tool-metric">
                            <span className="metric-label">Interviews Conducted</span>
                            <span className="metric-value">37</span>
                          </div>
                          <div className="tool-metric">
                            <span className="metric-label">Avg. Response Time</span>
                            <span className="metric-value">4.2 min</span>
                          </div>
                        </div>
                        <div className="tool-status-bar">
                          <span className="tool-status active">Active</span>
                          <button className="btn-primary">Configure</button>
                        </div>
                      </div>
                      
                      <div className="tool-card">
                        <div className="tool-header">
                          <MdExtension className="tool-icon" />
                          <h5>COACH Chrome Extension</h5>
                        </div>
                        <p>Browser extension that acts as a virtual mentor, explaining alerts and guiding analysts through investigation steps.</p>
                        <div className="tool-status-bar">
                          <span className="tool-status active">Available</span>
                          <button className="btn-secondary">Download</button>
                        </div>
                      </div>
                      
                      <div className="tool-card">
                        <div className="tool-header">
                          <MdAutoFixHigh className="tool-icon" />
                          <h5>Investigation Simulator</h5>
                        </div>
                        <p>Training tool that creates realistic security scenarios for analysts to practice, with AI feedback on investigation quality.</p>
                        <div className="tool-status-bar">
                          <span className="tool-status">Coming Soon</span>
                          <button className="btn-secondary" disabled>Preview</button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* AI Chat Interface */}
              {chatOpen && (
                <div className="ai-chat-overlay">
                  <div className="ai-chat-container">
                    <div className="ai-chat-header">
                      <h4>AI Security Assistant</h4>
                      <button className="close-btn" onClick={() => setChatOpen(false)}>×</button>
                    </div>
                    <div className="ai-chat-messages">
                      {chatMessages.map((message, index) => (
                        <div key={index} className={`chat-message ${message.role}`}>
                          <div className="message-content">{message.content}</div>
                        </div>
                      ))}
                    </div>
                    <div className="ai-chat-input">
                      <input 
                        type="text" 
                        placeholder="Ask about an alert or investigation..." 
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleChatSend()}
                      />
                      <button onClick={handleChatSend}>Send</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 